import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queryKeys } from '@/shared/constants/query-keys'
import type {
  IAvailabilityMetrics,
  IInventoryDetail,
  IInventoryItem,
  IListReservationHistory,
  IMonthlyActivity,
  IReservation,
  ISoldByProject
} from '../models/inventory'
import {
  getAvailability,
  getAvailabilityMetrics,
  getMonthlyActivity,
  getPropertyDetail,
  getReservationByProperty,
  getReservationHistory
} from '../services/inventory.services'

export const useInventoryQuery = (dependence = true) => {
  return useQuery<IInventoryItem[]>({
    queryKey: queryKeys.availability.list(),
    queryFn: async () => {
      try {
        return await getAvailability()
      } catch (e) {
        console.error(e)
        return []
      }
    },
    refetchOnWindowFocus: false,
    enabled: dependence
  })
}

export const useInventoryMetricsQuery = (dependence = true) => {
  return useQuery<IAvailabilityMetrics>({
    queryKey: queryKeys.availability.metrics(),
    queryFn: async () => {
      return await getAvailabilityMetrics()
    },
    refetchOnWindowFocus: false,
    enabled: dependence
  })
}

export const useMonthlyActivityQuery = () => {
  return useQuery<IMonthlyActivity>({
    queryKey: queryKeys.availability.monthlyActivity(),
    queryFn: getMonthlyActivity,
    refetchOnWindowFocus: false
  })
}

export function useSoldByProject(data: IInventoryItem[]): ISoldByProject[] {
  return useMemo(() => {
    const soldItems = data.filter((d) => d.availabilityStatus === 'SOLD')
    const total = soldItems.length
    if (total === 0) return []

    const grouped = new Map<string, number>()
    const projects = new Map<string, string>()

    for (const d of soldItems) {
      const name = d.project?.name ?? 'Sin proyecto'
      const code = d.project?.code ?? 'Sin proyecto'
      grouped.set(name, (grouped.get(name) ?? 0) + 1)
      projects.set(name, code)
    }

    return Array.from(grouped.entries())
      .map(([projectName, count]) => ({
        projectName,
        projectCode: projects.get(projectName) ?? '',
        count,
        percentage: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count)
  }, [data])
}

export function useInventoryMetrics(data: IInventoryItem[]): IAvailabilityMetrics {
  return useMemo(() => {
    const m: IAvailabilityMetrics = {
      total: 0,
      available: 0,
      reserved: 0,
      pendingAuthorization: 0,
      inNegotiation: 0,
      sold: 0
    }
    for (const d of data) {
      m.total++
      if (d.availabilityStatus === 'AVAILABLE') m.available++
      else if (d.availabilityStatus === 'RESERVED') m.reserved++
      else if (d.availabilityStatus === 'PENDING_AUTHORIZATION') m.pendingAuthorization++
      else if (d.availabilityStatus === 'IN_NEGOTIATION') m.inNegotiation++
      else if (d.availabilityStatus === 'SOLD') m.sold++
    }
    return m
  }, [data])
}

export const usePropertyDetail = (propertyId: string | null) => {
  return useQuery<IInventoryDetail | null>({
    queryKey: queryKeys.availability.detail(propertyId ?? ''),
    queryFn: async () => {
      if (!propertyId) return null
      return await getPropertyDetail(propertyId)
    },
    refetchOnWindowFocus: false,
    enabled: !!propertyId
  })
}

export const useReservationByProperty = (propertyId: string | null) => {
  return useQuery<IReservation | null>({
    queryKey: queryKeys.reservations.byProperty(propertyId ?? ''),
    queryFn: async () => {
      if (!propertyId) return null
      return await getReservationByProperty(propertyId)
    },
    refetchOnWindowFocus: false,
    enabled: !!propertyId
  })
}

export const useReservationHistory = (propertyId: string | null) => {
  return useQuery<IListReservationHistory[]>({
    queryKey: queryKeys.reservations.history(propertyId ?? ''),
    queryFn: async () => {
      if (!propertyId) return []
      return await getReservationHistory(propertyId)
    },
    refetchOnWindowFocus: false,
    enabled: !!propertyId
  })
}

// Los hooks de comentarios viven en `use-inventory-comments.ts`: eligen entre v1 y v2 y devuelven
// un view model único, cosa que no tiene nada que ver con lo que hace este archivo.
